// client/src/pages/business/BusinessProfile.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LanguageSelector from '../../components/common/LanguageSelector';
import EmailPreferences from '../../components/common/EmailPreferences';
import PasswordField from '../../components/common/PasswordField';
// ✅ IMPORT API_URL alongside businessAPI
import { businessAPI, API_URL } from '../../services/api';
import toast from 'react-hot-toast';

// Service Areas Configuration
const SERVICE_AREAS = {
  crete: {
    label: 'Κρήτη (Crete)',
    areas: [
      { value: 'heraklion', label: 'Ηράκλειο (Heraklion)' },
      { value: 'chania', label: 'Χανιά (Chania)' },
      { value: 'rethymno', label: 'Ρέθυμνο (Rethymno)' },
      { value: 'lasithi', label: 'Λασίθι (Lasithi)' }
    ]
  },
  attica: {
    label: 'Αττική (Attica)',
    areas: [
      { value: 'athens_central', label: 'Κεντρική Αθήνα (Central Athens)' },
      { value: 'athens_north', label: 'Βόρεια Αθήνα (North Athens)' },
      { value: 'athens_south', label: 'Νότια Αθήνα (South Athens)' },
      { value: 'athens_east', label: 'Ανατολική Αθήνα (East Athens)' },
      { value: 'athens_west', label: 'Δυτική Αθήνα (West Athens)' },
      { value: 'piraeus', label: 'Πειραιάς (Piraeus)' },
      { value: 'east_attica', label: 'Ανατολική Αττική (East Attica)' },
      { value: 'west_attica', label: 'Δυτική Αττική (West Attica)' }
    ]
  },
  central: {
    label: 'Κεντρική Ελλάδα (Central Greece)',
    areas: [
      { value: 'thessaloniki', label: 'Θεσσαλονίκη (Thessaloniki)' },
      { value: 'central_macedonia', label: 'Κεντρική Μακεδονία (Central Macedonia)' },
      { value: 'larissa', label: 'Λάρισα (Larissa)' },
      { value: 'volos', label: 'Βόλος (Volos)' }
    ]
  },
  islands: {
    label: 'Νησιά (Islands)',
    areas: [
      { value: 'rhodes', label: 'Ρόδος (Rhodes)' },
      { value: 'corfu', label: 'Κέρκυρα (Corfu)' },
      { value: 'mykonos', label: 'Μύκονος (Mykonos)' },
      { value: 'santorini', label: 'Σαντορίνη (Santorini)' }
    ]
  }
};

const BusinessProfile = () => {
  const { t } = useTranslation('business');
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userLanguage, setUserLanguage] = useState('el');
  const [lockedFields, setLockedFields] = useState([]);
  const isLocked = (f) => lockedFields.includes(f);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    // ✅ ADDED: New Fields in State
    legalName: '',
    vatNumber: '',
    taxOffice: '',
    // End new fields
    street: '',
    city: '',
    zipCode: '',
    phone: '',
    email: '',
    contactPerson: '',
    serviceAreas: []  
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await businessAPI.getProfile();
      setProfile(response.data);
      
      const business = response.data.business;
      const user = response.data.user;

      // ✅ FIX: Check ALL possible locations for email/phone data to ensure display
      setFormData({
        businessName: business.businessName || '',
        businessType: business.businessType || '',
        // ✅ ADDED: Populate New Fields
        legalName: business.legalName || '',
        vatNumber: business.vatNumber || '',
        taxOffice: business.taxOffice || '',
        // End new fields
        street: business.address?.street || '',
        city: business.address?.city || '',
        zipCode: business.address?.zipCode || business.address?.postalCode || '',
        
        // Priority: Business Level -> Contact Object -> User Profile
        phone: business.phone || business.contact?.phone || user?.profile?.phone || '',
        email: business.email || business.contact?.email || user?.email || '',
        contactPerson: business.contactPerson || business.contact?.contactPerson || user?.profile?.name || '',
        
        serviceAreas: business.serviceAreas || []  
      });

      setLockedFields(response.data.lockedFields || []);
      setUserLanguage(user?.language || 'el');
    } catch (err) {
      setError(t('profile.error'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceAreaToggle = (areaValue) => {
    const currentAreas = formData.serviceAreas;
    const newAreas = currentAreas.includes(areaValue)
      ? currentAreas.filter(a => a !== areaValue)
      : [...currentAreas, areaValue];
    
    setFormData({
      ...formData,
      serviceAreas: newAreas
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (formData.serviceAreas.length === 0) {
      setError(t('profile.serviceAreasRequired'));
      return;
    }
    
    setSaving(true);

    try {
      await businessAPI.updateProfile({
        businessName: formData.businessName,
        businessType: formData.businessType,
        // ✅ ADDED: Send new fields
        legalName: formData.legalName,
        vatNumber: formData.vatNumber,
        taxOffice: formData.taxOffice,
        // End new fields
        address: {
          street: formData.street,
          city: formData.city,
          zipCode: formData.zipCode
        },
        contact: {
          phone: formData.phone,
          email: formData.email,
          contactPerson: formData.contactPerson
        },
        // Also update root level fields
        phone: formData.phone,
        email: formData.email,
        contactPerson: formData.contactPerson,
        serviceAreas: formData.serviceAreas  
      });

      setSuccess(t('profile.saved'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || t('profile.error'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('profile.passwordMismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError(t('profile.passwordMinError'));
      return;
    }

    setSaving(true);

    try {
      await businessAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setSuccess(t('profile.passwordChanged'));
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || t('profile.passwordError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setError(t('profile.deleteConfirmError'));
      return;
    }
    
    setDeleting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      // ✅ FIXED: Using dynamic API_URL
      const response = await fetch(`${API_URL}/auth/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: deletePassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(t('profile.accountDeleted'));
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/');
      } else {
        setError(data.message || t('profile.deleteFailedGeneric'));
        setDeleting(false);
      }
    } catch (err) {
      setError(t('profile.deleteError'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
  <div className="min-h-screen bg-gray-50">
    <Navbar />
    <div className="pt-16 lg:pt-0">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">{t('profile.title')}</h1>

        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        <LanguageSelector 
          userType="business"
          currentLanguage={userLanguage}
          onLanguageChange={setUserLanguage}
        />

        <EmailPreferences userRole="business" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-6">{t('profile.businessInfo')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.businessName')} *
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  disabled={isLocked('businessName')}
                  className={`w-full border border-gray-300 rounded-md p-2 ${isLocked('businessName') ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                />
                {isLocked('businessName') && (
                  <p className="text-xs text-gray-400 mt-1">{t('profile.fieldLocked', 'Cannot be changed after account approval — contact support.')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.businessType')} *
                </label>
                <select
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">{t('profile.selectBusinessType')}</option>
                  <option value="restaurant">{t('businessTypes.restaurant')}</option>
                  <option value="cafe">{t('businessTypes.cafe')}</option>
                  <option value="hotel">{t('businessTypes.hotel')}</option>
                  <option value="catering">{t('businessTypes.catering')}</option>
                  <option value="retail">{t('businessTypes.retail')}</option>
                  <option value="bakery">{t('businessTypes.bakery')}</option>
                  <option value="bar">{t('businessTypes.bar')}</option>
                  <option value="other">{t('businessTypes.other')}</option>
                </select>
              </div>

              {/* ✅ ADDED: Legal Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.legalName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="legalName"
                  value={formData.legalName}
                  onChange={handleChange}
                  required
                  disabled={isLocked('legalName')}
                  className={`w-full border border-gray-300 rounded-md p-2 ${isLocked('legalName') ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                />
                {isLocked('legalName') && (
                  <p className="text-xs text-gray-400 mt-1">{t('profile.fieldLocked', 'Cannot be changed after account approval — contact support.')}</p>
                )}
              </div>

              {/* ✅ ADDED: VAT Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.vatNumberLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="vatNumber"
                  value={formData.vatNumber}
                  onChange={handleChange}
                  required
                  disabled={isLocked('vatNumber')}
                  className={`w-full border border-gray-300 rounded-md p-2 ${isLocked('vatNumber') ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                />
                {isLocked('vatNumber') && (
                  <p className="text-xs text-gray-400 mt-1">{t('profile.fieldLocked', 'Cannot be changed after account approval — contact support.')}</p>
                )}
              </div>

              {/* ✅ ADDED: Tax Office */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.taxOfficeLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="taxOffice"
                  value={formData.taxOffice}
                  onChange={handleChange}
                  required
                  disabled={isLocked('taxOffice')}
                  className={`w-full border border-gray-300 rounded-md p-2 ${isLocked('taxOffice') ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                />
                {isLocked('taxOffice') && (
                  <p className="text-xs text-gray-400 mt-1">{t('profile.fieldLocked', 'Cannot be changed after account approval — contact support.')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.street')}
                </label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.city')}
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.postalCode')}
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.contactPerson')}
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('profile.serviceAreasTitle')} *
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('profile.serviceAreasHint')}
                </p>
                
                <div className="space-y-4 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
                  {Object.entries(SERVICE_AREAS).map(([regionKey, region]) => (
                    <div key={regionKey} className="space-y-2">
                      <h4 className="font-medium text-gray-700 text-sm">{region.label}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-4">
                        {region.areas.map(area => (
                          <label key={area.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={formData.serviceAreas.includes(area.value)}
                              onChange={() => handleServiceAreaToggle(area.value)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{area.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {formData.serviceAreas.length > 0 && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                    <p className="text-sm text-green-700">
                      {t('profile.selectedAreas', { count: formData.serviceAreas.length })}
                    </p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? t('profile.saving') : t('profile.save')}
              </button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-6">{t('profile.changePassword')}</h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.currentPassword')} *
                </label>
                <PasswordField
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.newPassword')} *
                </label>
                <PasswordField
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
                <p className="text-xs text-gray-500 mt-1">{t('profile.minPasswordLength')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.confirmPassword')} *
                </label>
                <PasswordField
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? t('profile.saving') : t('profile.changePassword')}
              </button>
            </form>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-sm text-gray-700 mb-2">{t('profile.contactInfo')}</h3>
              <p className="text-sm text-gray-600">
                <strong>{t('profile.email')}:</strong> {profile?.user?.email}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-red-600 mb-4">
            {t('profile.deleteAccountTitle')}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('profile.deleteWarning')}
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 mb-4 ml-4">
            <li>{t('profile.deleteItem1')}</li>
            <li>{t('profile.deleteItem2')}</li>
            <li>{t('profile.deleteItem3')}</li>
            <li>{t('profile.deleteItem4')}</li>
          </ul>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            {t('profile.deleteMyAccount')}
          </button>
        </div>
      </main>
    <Footer />
    </div>

    {showDeleteModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
          <h3 className="text-lg font-bold text-red-600 mb-4">
            {t('profile.confirmDeletion')}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('profile.willPermanentlyDelete')}
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 mb-4">
            <li>{t('profile.deleteItem1')}</li>
            <li>{t('myOrders.title', 'All your orders')}</li>
            <li>{t('profile.deleteItem3')}</li>
            <li>{t('profile.deleteItem4')}</li>
          </ul>
          <p className="text-sm font-bold text-red-600 mb-4">
            {t('profile.cannotBeUndone')}
          </p>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.enterPasswordConfirm')}
            </label>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder={t('profile.passwordPlaceholder')}
              disabled={deleting}
            />
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeletePassword('');
                setError('');
              }}
              disabled={deleting}
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
            >
              {t('profile.cancel')}
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={!deletePassword || deleting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? t('profile.deleting') : t('profile.deleteAccount')}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

export default BusinessProfile;